export const GURS_CONSTANTS = {
  ID_FIELDS: {
    'KN_SLO_PARCELE_SLO_PARCELE_poligon': 'EID_PARCELA',
    'KN_SLO_STAVBE': 'EID_STAVBA',
    'KN_SLO_NASLOVI_HS': 'HS_MID',
    'gsx8': ['EID', 'SIF_VRSTE'] as string[],
  },

  // `sourcePrefixes` are matched as EXACT source ids by GursDataProvider
  // (getSelectableLayers / isGursLayer) — the name is historical. A tenant source
  // that merely starts with one of them (gji_vp_<type>_tiles) is NOT a GURS layer.
  LAYER_GROUPS: {
    GJI: {
      id: 'GJI',
      sourcePrefixes: ['gji'],
      categories: ['gji', 'gsx8_special'],
    },
    KN_PARCELE: {
      id: 'KN_PARCELE',
      sourcePrefixes: ['gurs_zk'],
      categories: ['zkn', 'zkn_3d', 'zkn_m2', 'upravni_status'],
    },
    KS_STAVBE: {
      id: 'KS_STAVBE',
      sourcePrefixes: ['stavbe'],
      categories: ['ks', 'ks3d', 'ks_komunalni', 'ks_leto_izgradnje'],
    },
    NASLOVI: {
      id: 'NASLOVI',
      sourcePrefixes: ['naslovi'],
      categories: ['naslovi', 'rpe'],
    },
  } as const,

  VARIANT_LAYERS: {
    M2: '_M2',
    BUILDINGS_3D: '_3D',
    KOMUNALNI: '_komunalni',
    LETO_IZGRADNJE: '_leto_izgradnje',
    UPRAVNI_STATUS: 'parcele_upravni_status',
  } as const,

  // `metadata['gurs:category']` values, for code that switches a whole layer FAMILY on and off
  // rather than one id (the parcel-history map's overlays). Preferred over an id substring where
  // the family has several layers or might be renamed — KO is two layers, a boundary and its name.
  // Guarded against gursLayers.ts by tests/unit/client/gursZoomThresholds.test.ts.
  LAYER_CATEGORIES: {
    UPRAVNI_STATUS: 'upravni_status',
    KO: 'ko',
  } as const,

  // Line colours of the layers a UI can key a swatch off, so a chip promising a colour names the
  // one actually painted. Mirrored from gursLayers.ts rather than imported: that module is
  // build-time (1700 lines of style generation) and no client bundle carries it. Guarded against
  // the real paint by tests/unit/client/gursZoomThresholds.test.ts.
  // Only KO so far — upravni status colours each boundary BY its status, so it has no one colour.
  LAYER_COLORS: {
    KO: 'rgb(127, 0, 63)',
  } as const,

  EXCLUDED_FROM_SELECTION: [
    // #276: Občine (and KO/Naselja/Ulice) are now clickable, so Občine is no
    // longer excluded; only the hidden upravni_status overlay stays non-selectable.
    'parcele_upravni_status',
  ] as const,

  // Zoom onsets for the toolbar/chip "active but not visible yet" state.
  // MUST match the real layer minzooms in src/shared/map/gursLayers.ts —
  // guarded by tests/unit/client/gursZoomThresholds.test.ts. The old values
  // predated the #304/#307 tile rebuilds and lied in both directions
  // (komunalni claimed 14 but renders from 16; leto izgradnje 14 vs 17).
  ZOOM_THRESHOLDS: {
    gji: 0,
    parcele: 14, // KN_SLO_PARCELE_* stroke/label minzoom 14 (#304/#307)
    stavbe: 13, // stavbe source minzoom 13 (#307)
    naslovi: 16, // KN_SLO_NASLOVI_HS_label minzoom 16
    parceleVrednost: 14, // _M2 variants minzoom 14
    view3d: 14,
    komunalniPrikljucki: 16, // _komunalni variants minzoom 16
    letoIzgradnje: 17, // _leto_izgradnje variants minzoom 17
    upravniStatus: 14, // gurs_zk source minzoom 14 (layer itself unbounded)
    // KO is the other way round from upravniStatus: the `naslovi` source starts at z0, so the
    // LAYER's own minzoom 11 is what bounds it (#276). The KO name label additionally stops at
    // z15, by design — parcel numbers take over above that.
    ko: 11,
  } as const,
} as const;

export function isGursSourceLayer(sourceLayer: string): boolean {
  return (
    sourceLayer.startsWith('KN_SLO_') ||
    sourceLayer.startsWith('RPE_SLO_') ||
    sourceLayer.startsWith('gsx8_')
  );
}

export function isStavbeSourceLayer(sourceLayer: string): boolean {
  return sourceLayer.startsWith('KN_SLO_STAVBE');
}

export function isGjiSourceLayer(sourceLayer: string): boolean {
  return sourceLayer === 'gsx8_default' || sourceLayer.startsWith('gsx8_');
}

/** #752 gradbene parcele: the MVT source-layer in the gurs_rpe archive (style source `naslovi`). */
export const GRADBENE_PARCELE_SOURCE_LAYER = 'KN_SLO_PARCELE_SLO_GRADBENE_PARCELE_poligon';

/**
 * #741: the invisible fills that make a whole parcel a click target — one per parcel
 * variant (2D, 2.5D). Exact ids: a tenant layer that ends in `_hit` is never one of them.
 * Mirrored from gursLayers.ts, which no client bundle carries; guarded against it by
 * tests/unit/client/gursParcelHitLayers.test.ts.
 */
export const PARCEL_HIT_LAYER_IDS: readonly string[] = [
  'KN_SLO_PARCELE_SLO_PARCELE_poligon_hit',
  'KN_SLO_PARCELE_SLO_PARCELE_poligon_3D_hit',
];

export function isParcelHitLayer(layerId: string | undefined): boolean {
  return layerId !== undefined && PARCEL_HIT_LAYER_IDS.includes(layerId);
}

type Props = Record<string, unknown> | null | undefined;

/**
 * #754: a GURS id from a TILE feature (uppercase MVT key) or an API feature
 * (lowercase: /api/v1/gurs/info passes the column names through). The popup cards
 * hold API features, so a match that reads one case only compares undefined with
 * undefined and every card resolves to the first feature of its layer. Null when
 * neither is set; a number becomes a string.
 */
export function gursIdOf(props: Props, key: string): string | null {
  const v = props?.[key] ?? props?.[key.toLowerCase()];
  if (typeof v === 'number') return String(v);
  return typeof v === 'string' && v !== '' ? v : null;
}

/** #752: a building plot's EID, either key case. */
export function gradbenaParcelaEid(props: Props): string | null {
  return gursIdOf(props, 'EID_GRADBENA_PARCELA');
}

/** The uppercase tile key(s) that identify a feature of a GURS card layer. */
function gursIdKeys(sourceLayer: string): readonly string[] {
  if (sourceLayer === 'KN_SLO_NASLOVI_HS') return ['HS_MID'];
  if (isStavbeSourceLayer(sourceLayer)) return ['EID_STAVBA'];
  if (sourceLayer === 'KN_SLO_PARCELE_SLO_PARCELE_poligon') return ['EID_PARCELA'];
  if (sourceLayer === 'KN_SLO_KAT_OBCINE_KATASTRSKE_OBCINE_poligon') return ['EID_KATAST'];
  if (sourceLayer === 'RPE_SLO_PROSTORSKE_ENOTE_OBCINE_poligon') return ['EID_OBCINA'];
  if (sourceLayer === 'RPE_SLO_PROSTORSKE_ENOTE_NASELJA_poligon') return ['EID_NASELJE'];
  if (sourceLayer === 'RPE_SLO_ULICE_ULICE_line') return ['EID_ULICA'];
  if (sourceLayer === GRADBENE_PARCELE_SOURCE_LAYER) return ['EID_GRADBENA_PARCELA'];
  return ['EID', 'SIF_VRSTE']; // GJI
}

/**
 * #754: the identity of a GURS card feature, read in either key case — one id, or
 * `EID|SIF_VRSTE` for GJI. Null when any part is missing: a feature without an id
 * matches nothing.
 */
export function gursFeatureKey(sourceLayer: string, props: Props): string | null {
  const parts = gursIdKeys(sourceLayer).map((key) => gursIdOf(props, key));
  return parts.every((p) => p !== null) ? parts.join('|') : null;
}

export function getIdField(sourceLayer: string): string | string[] {
  if (sourceLayer === GURS_CONSTANTS.ID_FIELDS['KN_SLO_PARCELE_SLO_PARCELE_poligon']) {
    return GURS_CONSTANTS.ID_FIELDS['KN_SLO_PARCELE_SLO_PARCELE_poligon'];
  }
  if (isStavbeSourceLayer(sourceLayer)) {
    return GURS_CONSTANTS.ID_FIELDS['KN_SLO_STAVBE'];
  }
  if (sourceLayer === GURS_CONSTANTS.ID_FIELDS['KN_SLO_NASLOVI_HS']) {
    return GURS_CONSTANTS.ID_FIELDS['KN_SLO_NASLOVI_HS'];
  }
  if (isGjiSourceLayer(sourceLayer)) {
    return GURS_CONSTANTS.ID_FIELDS['gsx8'];
  }
  return 'EID';
}
